import * as React from "react";
import { useMutation } from "convex/react";
import { toast } from "sonner";
import { api } from "@/lib/convexApi";
import { toUserFacingErrorMessage } from "@/lib/errors";

const CANCELLABLE_STATUSES = new Set([
  "checkout_initiated", "pending_payment", "payment_processing",
  "awaiting_inventory", "ready_to_prepare", "ready_for_pickup", "in_transit", "on_hold",
]);

export function canStartAdminCancellation(order: { status?: string }): boolean {
  return CANCELLABLE_STATUSES.has(String(order.status ?? ""));
}

type CancellationOrder = {
  _id?: string;
  id?: string;
  orderNumber?: string;
  status?: string;
  totalAmount?: number;
};

export function useAdminOrderCancellation(authUserId?: string) {
  const beginOrderWorkflow = useMutation(api.adminOrderWorkflow.beginOrderWorkflow);
  const [order, setOrder] = React.useState<CancellationOrder | null>(null);
  const [isStarting, setIsStarting] = React.useState(false);
  const startingRef = React.useRef(false);
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const start = React.useCallback(async (candidate: CancellationOrder) => {
    if (startingRef.current) return;
    const orderId = candidate._id ?? candidate.id;
    if (!authUserId || !orderId) {
      toast.error("Sign in as an admin and select an order to cancel.");
      return;
    }
    startingRef.current = true;
    setIsStarting(true);
    try {
      await beginOrderWorkflow({ authUserId, orderId: orderId as any, reason: "cancellation" });
      if (mountedRef.current) setOrder({ ...candidate, status: "on_hold" });
    } catch (error: unknown) {
      toast.error(toUserFacingErrorMessage(error, "Unable to put the order on hold. Try again."));
    } finally {
      startingRef.current = false;
      if (mountedRef.current) setIsStarting(false);
    }
  }, [authUserId, beginOrderWorkflow]);

  const close = React.useCallback(() => setOrder(null), []);
  return { order, isStarting, start, close };
}
